import React, { useMemo, useState } from 'react';
import {
  App,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';

const PositionsPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.positions(),
    queryFn: () => hrmsService.listPositions(),
  });

  const { data: grades = [] } = useQuery({
    queryKey: hrmsKeys.grades(),
    queryFn: () => hrmsService.listGrades(),
  });

  const { data: departments = [] } = useDepartmentsForSelection();

  const gradeOptions = grades.map((g: any) => ({
    value: g.id,
    label: `${g.grade_code || ''} — ${g.grade_name || g.name || ''}`.trim(),
  }));

  const departmentLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of departments) {
      map.set(d.id, `${d.department_code} - ${d.department_name}`);
    }
    return map;
  }, [departments]);

  const gradeLabel = (id?: string) =>
    gradeOptions.find((o) => o.value === id)?.label || '—';

  const departmentLabel = (id?: string | null) =>
    (id && departmentLabelById.get(id)) || '—';

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, sort_order: 0 });
    setDrawerOpen(true);
  };

  const openEdit = (r: any) => {
    setEditing(r);
    form.setFieldsValue({
      position_code: r.position_code || r.code,
      position_name: r.position_name || r.name,
      grade_id: r.grade_id || undefined,
      department_id: r.department_id || undefined,
      sort_order: r.sort_order ?? 0,
      is_active: r.is_active !== false,
    });
    setDrawerOpen(true);
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      const body = {
        position_code: v.position_code,
        position_name: v.position_name,
        grade_id: v.grade_id || null,
        department_id: v.department_id || null,
        sort_order: v.sort_order ?? 0,
        is_active: v.is_active,
      };
      if (editing) await hrmsService.updatePosition(editing.id, body);
      else await hrmsService.createPosition(body);
      message.success(editing ? 'Updated' : 'Created');
      setDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: hrmsKeys.positions() });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Save failed');
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Code',
      dataIndex: 'position_code',
      width: 120,
      render: (v, r) => v || r.code || '—',
    },
    {
      title: 'Name',
      dataIndex: 'position_name',
      render: (v, r) => v || r.name || '—',
    },
    {
      title: 'Grade',
      dataIndex: 'grade_id',
      ellipsis: true,
      render: (id: string) => gradeLabel(id),
    },
    {
      title: 'Department',
      dataIndex: 'department_id',
      ellipsis: true,
      render: (id: string) => departmentLabel(id),
    },
    {
      title: 'Sort',
      dataIndex: 'sort_order',
      width: 80,
      render: (v) => v ?? 0,
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 80,
      render: (v: boolean) => (
        <Tag color={v !== false ? 'green' : 'default'}>{v !== false ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'a',
      width: 80,
      render: (_, r) => (
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Positions"
          subtitle="Job positions with optional grade and department"
          primaryActions={
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add position
            </Button>
          }
        />
      }
    >
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20 }}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No positions yet" />
          ),
        }}
      />

      <Drawer
        title={editing ? 'Edit position' : 'New position'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={440}
        destroyOnClose
        extra={
          <Space>
            <Button type="primary" onClick={save}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="position_code" label="Code" rules={[{ required: true }]}>
            <Input disabled={Boolean(editing)} placeholder="ENG-SR" />
          </Form.Item>
          <Form.Item name="position_name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Senior Engineer" />
          </Form.Item>
          <Form.Item name="grade_id" label="Grade">
            <Select allowClear showSearch optionFilterProp="label" options={gradeOptions} />
          </Form.Item>
          <Form.Item name="department_id" label="Department">
            <DepartmentSelect allowClear placeholder="Select department" />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort order">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </WorkspaceLayout>
  );
};

export default PositionsPage;
